import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Clock } from "lucide-react";
import { api } from "@/lib/api";

const MAX_POLLS = 8;

export default function RecordsPaymentSuccessPage() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState("checking"); // checking | paid | pending | error
    const [amount, setAmount] = useState(null);

    const poll = useCallback(async (n) => {
        if (!sessionId) { setState("error"); return; }
        try {
            const r = await api.get(`/payments/record-request/status/${sessionId}`);
            if (r.data.amount != null) setAmount(r.data.amount / 100);
            if (r.data.payment_status === "paid") { setState("paid"); return; }
            if (r.data.status === "expired" || n >= MAX_POLLS) { setState("pending"); return; }
            setTimeout(() => poll(n + 1), 2000);
        } catch {
            if (n >= MAX_POLLS) setState("error"); else setTimeout(() => poll(n + 1), 2000);
        }
    }, [sessionId]);

    useEffect(() => { poll(0); }, [poll]);

    const icon = { checking: Loader2, paid: CheckCircle2, pending: Clock, error: XCircle }[state];
    const Icon = icon;
    const tone = { checking: "bg-slate-100 text-slate-600", paid: "bg-green-100 text-green-700", pending: "bg-amber-100 text-amber-700", error: "bg-red-100 text-red-700" }[state];

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="records-payment-success-page">
            <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center space-y-4">
                <span className={`w-14 h-14 mx-auto rounded-2xl flex items-center justify-center ${tone}`}>
                    <Icon className={`w-7 h-7 ${state === "checking" ? "animate-spin" : ""}`} />
                </span>
                {state === "checking" && <><h1 className="font-display font-bold text-2xl text-phoenix-navy">Confirming your payment…</h1><p className="text-sm text-muted-foreground">This only takes a moment.</p></>}
                {state === "paid" && <><h1 className="font-display font-bold text-2xl text-phoenix-navy" data-testid="payment-paid">Payment received{amount ? ` — $${amount.toFixed(2)}` : ""}</h1><p className="text-sm text-muted-foreground">Thank you. The records team will release your records shortly.</p></>}
                {state === "pending" && <><h1 className="font-display font-bold text-2xl text-phoenix-navy">Payment processing</h1><p className="text-sm text-muted-foreground">If you completed checkout, your payment will be confirmed soon. You can close this page.</p></>}
                {state === "error" && <><h1 className="font-display font-bold text-2xl text-phoenix-navy">We couldn't confirm this payment</h1><p className="text-sm text-muted-foreground">Please contact the records office if you were charged.</p></>}
                <Link to="/" className="inline-block text-sm font-semibold text-phoenix-navy hover:underline">Return home</Link>
            </div>
        </div>
    );
}
